import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './CreateOKR.css';
import '../styles/okr-m3-tokens.css';
import { FaPlus, FaClock } from 'react-icons/fa';
import { AuthContext } from '../context/AuthContext';
import { calculateOKRStatus, OKR_STATUS_META } from '../utils/okrStatusUtils';

const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

export default function CreateOKR() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token } = useContext(AuthContext);
  const defaultType = location.state?.type || 'quarter';

  // Form state
  const [objective, setObjective] = useState('');
  const [deadline, setDeadline] = useState('');
  const [level, setLevel] = useState('company');

  // Owners
  const [owners, setOwners] = useState(['']);
  const [ownerOptions, setOwnerOptions] = useState([]);

  // Teams for KRs
  const [krTeamOptions, setKrTeamOptions] = useState([]);

  // Period
  const [year, setYear] = useState('2025');
  const [quarter, setQuarter] = useState(
    defaultType === 'quarter' ? 'Q2 (Apr-Jun)' : 'January'
  );

  // Key Results
  const [keyResults, setKeyResults] = useState([
    { text: '', team: '', krOwner: '', completed: false, deadline: '' }
  ]);

  // Score / Status
  const [score, setScore] = useState(0);
  const [status, setStatus] = useState('Not Started');

  // Automatically update status when keyResults or deadline change
  useEffect(() => {
    setStatus(calculateOKRStatus(keyResults, deadline));
  }, [keyResults, deadline]);

  // Compute % done
  const computeProgress = () => {
    const total = keyResults.length;
    const done = keyResults.filter(kr => kr.completed).length;
    return total ? Math.round((done / total) * 100) : 0;
  };

  // Helper: choose clock color based on days until due
  const getColor = dateStr => {
    if (!dateStr) return '#757575';
    const due = new Date(dateStr);
    const now = new Date();
    const diff = (due - now) / (1000 * 60 * 60 * 24);
    if (diff <= 0) return '#D32F2F';
    if (diff <= 2) return '#F57C00';
    return '#2E7D32';
  };

  // ─── Fetch owners ─────────────────────────────────────────────────────────
  useEffect(() => {
    async function fetchOwners() {
      try {
        const res = await fetch(`${API_BASE}/api/users`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) throw new Error('Failed to fetch owners');
        const data = await res.json();
        const list = Array.isArray(data)
          ? data
          : Array.isArray(data.users)
            ? data.users
            : [];
        const opts = list.map(u => u.name);
        setOwnerOptions(opts);
        setOwners(prev => {
          const mapped = prev.map(o => opts.includes(o) ? o : (opts[0] || ''));
          return mapped.length ? mapped.slice(0, 3) : [opts[0] || ''];
        });
      } catch (err) {
        console.error(err);
      }
    }
    fetchOwners();
  }, [token]);

  // ─── Fetch teams ──────────────────────────────────────────────────────────
  useEffect(() => {
    async function fetchTeams() {
      try {
        const res = await fetch(`${API_BASE}/api/teams`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) throw new Error('Failed to fetch teams');
        const data = await res.json();
        const list = Array.isArray(data)
          ? data
          : Array.isArray(data.teams)
            ? data.teams
            : [];
        setKrTeamOptions(list.map(t => t.name));
      } catch (err) {
        console.error(err);
      }
    }
    if (token) fetchTeams();
  }, [token]);

  // ─── Owner helpers ───────────────────────────────────────────────────────
  const updateOwner = (i, v) => setOwners(o => o.map((x, idx) => idx === i ? v : x));
  const addOwner = () => setOwners(o => o.length < 3 ? [...o, ownerOptions[0] || ''] : o);
  const removeOwner = i => setOwners(o => o.filter((_, idx) => idx !== i));

  // ─── Key-Result helpers ──────────────────────────────────────────────────
  const addKeyResult = () => setKeyResults(k => [...k, { text: '', team: '', krOwner: '', completed: false, deadline: '' }]);
  const updateKeyResult = (i, f, v) => setKeyResults(k => { const c = [...k]; c[i][f] = v; return c; });
  const toggleKRCompleted = i => setKeyResults(k => { const c = [...k]; c[i].completed = !c[i].completed; return c; });

  // ─── Redirect back to OKRs ────────────────────────────────────────────────
  const goHome = () => {
    if (['admin', 'super'].includes(user?.role)) {
      navigate('/company-okrs/quarter', { replace: true });
    } else {
      navigate('/company-okrs/my-okrs', { replace: true });
    }
  };
  const handleCancel = () => goHome();

  // ─── Submit new OKR ───────────────────────────────────────────────────────
  const handleSubmit = async () => {
    const computedStatus = calculateOKRStatus(keyResults, deadline);
    const payload = {
      objective,
      deadline,
      level,
      owners,
      year,
      quarter: defaultType === 'annual' ? 'Annual' : quarter,
      type: defaultType,
      keyResults: keyResults.map(kr => ({
        text: kr.text,
        team: kr.team,
        krOwner: kr.krOwner,
        completed: kr.completed,
        deadline: kr.deadline || ''
      })),
      progress: computeProgress(),
      score,
      status: computedStatus
    };

    try {
      const res = await fetch(`${API_BASE}/api/okrs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error('Failed to create OKR');
      goHome();
    } catch (err) {
      console.error(err);
      alert('Error creating OKR.');
    }
  };

  const getScoreLabel = (score) => {
    if (score < 0.5) return 'Low';
    if (score < 0.8) return 'Medium';
    return 'High';
  };

  const scoreLabel = getScoreLabel(score);
  const statusMeta = OKR_STATUS_META[status] || OKR_STATUS_META['Not Started'];

  return (
    <div className="m3-create-okr-page">
      <div className="m3-create-okr-header">
        <h1 className="m3-page-title">Create Objective</h1>
      </div>

      {/* CARD 1 — Objective Details */}
      <div className="m3-form-card">
        <h2 className="m3-card-title">Objective Details</h2>
        <div className="m3-form-grid">
          {/* Objective - Full Width */}
          <div className="m3-field-group m3-field-full">
            <label className="m3-field-label">Objective</label>
            <input
              className="m3-text-field"
              value={objective}
              onChange={e => setObjective(e.target.value)}
              placeholder="Type your objective here"
            />
          </div>

          {/* Deadline */}
          <div className="m3-field-group">
            <label className="m3-field-label">Deadline</label>
            <div className="m3-date-input-wrapper">
              <input
                type="date"
                className="m3-text-field"
                value={deadline}
                onChange={e => setDeadline(e.target.value)}
              />
              <FaClock className="m3-date-icon" style={{ color: getColor(deadline) }} />
            </div>
          </div>

          {/* Owners */}
          <div className="m3-field-group">
            <label className="m3-field-label">Owners</label>
            {owners.map((own, idx) => (
              <div key={idx} className="m3-owner-row">
                <select
                  className="m3-text-field"
                  value={own}
                  onChange={e => updateOwner(idx, e.target.value)}
                >
                  {ownerOptions.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
                {idx > 0 && (
                  <button
                    type="button"
                    className="m3-icon-button m3-icon-button-danger"
                    onClick={() => removeOwner(idx)}
                    title="Remove owner"
                  >
                    &minus;
                  </button>
                )}
              </div>
            ))}
            {owners.length < 3 && (
              <button
                type="button"
                className="m3-button m3-button-text m3-add-owner-btn"
                onClick={addOwner}
              >
                <FaPlus /> Add Owner
              </button>
            )}
          </div>

          {/* Level */}
          <div className="m3-field-group">
            <label className="m3-field-label">Level</label>
            <div className="m3-segmented-buttons">
              <button
                type="button"
                className={`m3-segmented-button ${level === 'company' ? 'active' : ''}`}
                onClick={() => setLevel('company')}
              >
                Company
              </button>
              <button
                type="button"
                className={`m3-segmented-button ${level === 'team' ? 'active' : ''}`}
                onClick={() => setLevel('team')}
              >
                Team
              </button>
            </div>
          </div>

          {/* Period */}
          <div className="m3-field-group">
            <label className="m3-field-label">Period</label>
            <div className="m3-period-inputs">
              <select
                className="m3-text-field"
                value={year}
                onChange={e => setYear(e.target.value)}
              >
                {Array.from({ length: 39 }, (_, i) => 2022 + i).map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
              {defaultType === 'quarter' ? (
                <select
                  className="m3-text-field"
                  value={quarter}
                  onChange={e => setQuarter(e.target.value)}
                >
                  <option>Q1 (Jan-Mar)</option>
                  <option>Q2 (Apr-Jun)</option>
                  <option>Q3 (Jul-Sep)</option>
                  <option>Q4 (Oct-Dec)</option>
                </select>
              ) : (
                <select
                  className="m3-text-field"
                  value={quarter}
                  onChange={e => setQuarter(e.target.value)}
                >
                  {[
                    'January', 'February', 'March', 'April', 'May', 'June',
                    'July', 'August', 'September', 'October', 'November', 'December'
                  ].map(m => <option key={m}>{m}</option>)}
                </select>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* CARD 2 — Score & Progress */}
      <div className="m3-form-card">
        <h2 className="m3-card-title">Score & Progress</h2>
        
        {/* Status (read-only) */}
        <div className="m3-field-group">
          <label className="m3-field-label">Status (auto-calculated)</label>
          <div className="m3-status-chip-readonly" style={{
            background: statusMeta.bg,
            color: statusMeta.color,
            borderColor: statusMeta.border
          }}>
            {statusMeta.icon} {status}
          </div>
        </div>

        {/* Progress (read-only) */}
        <div className="m3-field-group">
          <label className="m3-field-label">Progress (auto)</label>
          <div className="m3-progress-readonly">{computeProgress()}%</div>
        </div>

        {/* Score Slider */}
        <div className="m3-field-group">
          <label className="m3-field-label">Score</label>
          <div className="m3-score-slider-container">
            <div className="m3-score-labels">
              <span>0.0</span>
              <span>0.3</span>
              <span>0.5</span>
              <span>0.8</span>
              <span>1.0</span>
            </div>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              className="m3-score-slider"
              value={score}
              onChange={e => setScore(Number(e.target.value))}
            />
            <div className="m3-score-display">
              Score: {score.toFixed(2)} ({scoreLabel})
            </div>
          </div>
          <p className="m3-helper-text">
            Score is a manual rating of KR outcome quality (0.0–1.0).
          </p>
        </div>
      </div>

      {/* CARD 3 — Key Results */}
      <div className="m3-form-card">
        <h2 className="m3-card-title">Key Results</h2>
        {keyResults.map((kr, i) => (
          <div key={i} className="m3-kr-form-row">
            <div className="m3-kr-description-field">
              <label className="m3-field-label">Key Result {i + 1}</label>
              <input
                type="text"
                className="m3-text-field"
                placeholder="Key result description..."
                value={kr.text}
                onChange={e => updateKeyResult(i, 'text', e.target.value)}
              />
            </div>
            <div className="m3-kr-meta-grid">
              <div className="m3-kr-meta-field">
                <label className="m3-field-label">Deadline</label>
                <div className="m3-date-input-wrapper">
                  <input
                    type="date"
                    className="m3-text-field"
                    value={kr.deadline}
                    onChange={e => updateKeyResult(i, 'deadline', e.target.value)}
                  />
                  <FaClock
                    className="m3-date-icon"
                    style={{ color: getColor(kr.deadline) }}
                  />
                </div>
              </div>
              <div className="m3-kr-meta-field">
                <label className="m3-field-label">Team</label>
                <select
                  className="m3-text-field"
                  value={kr.team}
                  onChange={e => updateKeyResult(i, 'team', e.target.value)}
                >
                  <option value="">None</option>
                  {krTeamOptions.map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div className="m3-kr-meta-field">
                <label className="m3-field-label">Owner</label>
                <select
                  className="m3-text-field"
                  value={kr.krOwner}
                  onChange={e => updateKeyResult(i, 'krOwner', e.target.value)}
                >
                  <option value="">None</option>
                  {ownerOptions.map(o => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              </div>
              <div className="m3-kr-meta-field m3-kr-checkbox-field">
                <label className="m3-field-label">Done</label>
                <input
                  type="checkbox"
                  className="m3-checkbox"
                  checked={kr.completed}
                  onChange={() => toggleKRCompleted(i)}
                />
              </div>
            </div>
          </div>
        ))}
        <button
          type="button"
          className="m3-button m3-button-text m3-add-kr-btn"
          onClick={addKeyResult}
        >
          <FaPlus /> Add Key Result
        </button>
      </div>

      {/* Action Buttons */}
      <div className="m3-form-actions">
        <button className="m3-button m3-button-text" onClick={handleCancel}>
          Cancel
        </button>
        <button className="m3-button m3-button-filled" onClick={handleSubmit}>
          Submit
        </button>
      </div>
    </div>
  );
}
